import { useState, useEffect, useRef } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import MobileNav from './MobileNav';
import ConfiguracoesRail from './ConfiguracoesRail';
import BuscaGlobal from './BuscaGlobal';
import { ehRotaDeConfiguracao } from '../config/configuracoes';
import SubscriptionExpiredModal from './SubscriptionExpiredModal';
import SupportModeBanner from './SupportModeBanner';
import { ASSINATURA_VENCIDA_EVENT, FUNCIONALIDADE_DESLIGADA_EVENT, PERMISSAO_NEGADA_EVENT } from '../services/api';
import aviso from '../lib/aviso';
import { relerUsuario, USUARIO_ATUALIZADO_EVENT } from '../services/usuarioAtual';
import { FunilService } from '../services/FunilService';
import { definirEtapasDaEmpresa } from '../data/etapas';
import { canAccessRoute, getFirstAccessibleRoute, getUserPermissions } from '../config/permissions';
import { GlobalNotificationProvider } from '../contexts/GlobalNotificationContext';
import { PrimeirosPassosProvider } from '../contexts/PrimeirosPassosContext';
import { AgenteProvider } from '../contexts/AgenteContext';
import AgentePainel from './AgentePainel';
import NovidadeModal from './NovidadeModal';
import { AvisosNoAparelhoBanner } from './AvisosNoAparelho';
import { sincronizarAviso, soltarAvisoNoLogout } from '../lib/avisoNoAparelho';
// import { NvoipDialer } from './NvoipDialer';
const MainLayout = () => {
    const navigate = useNavigate();
    const location = useLocation();

    /* Guarda a última tela de OPERAÇÃO para o "voltar" do trilho levar de volta
       exatamente para ela. Sem isso, quem entrou em Configurações a partir do
       Chat voltaria para um padrão fixo — e o custo de entrar no modo passaria
       a ser perder o lugar. Um `ref` porque isto não redesenha nada sozinho. */
    /* O funil da empresa, uma vez por sessão: é dele que sai o nome de cada etapa nas seis telas
       que mostram etapa. Falhar aqui não quebra nada — os nomes que o produto entrega seguem
       valendo (`data/etapas`). */
    useEffect(() => {
        FunilService.listar()
            .then(definirEtapasDaEmpresa)
            .catch(() => { /* fica com os nomes padrão */ });
    }, []);

    const emConfiguracoes = ehRotaDeConfiguracao(location.pathname);
    const ultimaOperacional = useRef('/monitoramento');
    useEffect(() => {
        if (!emConfiguracoes) ultimaOperacional.current = location.pathname;
    }, [location.pathname, emConfiguracoes]);
    const voltarPara = ultimaOperacional.current;

    /* Só o que a modal de assinatura precisa. O `empresaId` saiu daqui junto
       com a gaveta: a regra "Meu plano some para as empresas 4 e 8" mora agora
       no `MobileNav`, que é quem desenha o item. */
    let assinaturaVencida = false;
    let dataExpiracaoPlano: string | null = null;
    try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        assinaturaVencida = user?.assinaturaVencida === true;
        dataExpiracaoPlano = user?.dataExpiracaoPlano ?? null;
    } catch (e) {
        // ignore
    }

    const [isSubscriptionExpiredOpen, setIsSubscriptionExpiredOpen] = useState(assinaturaVencida);

    // Abre a modal sempre que uma ação de criar/editar for bloqueada por assinatura vencida
    useEffect(() => {
        const handleAssinaturaVencidaBloqueio = () => setIsSubscriptionExpiredOpen(true);
        window.addEventListener(ASSINATURA_VENCIDA_EVENT, handleAssinaturaVencidaBloqueio);
        return () => window.removeEventListener(ASSINATURA_VENCIDA_EVENT, handleAssinaturaVencidaBloqueio);
    }, []);

    /* O `user` do localStorage deixou de ser foto do login: é relido quando a aba
       volta ao foco (no máximo a cada 5 min) e na hora em que a API recusa uma
       funcionalidade desligada. Quando muda, o layout redesenha — a sidebar lê as
       funcionalidades de novo — e, se a tela aberta deixou de ser acessível, a
       pessoa vai para a primeira que é. Sem isso, desligar em Sistema ›
       Funcionalidades só teria efeito no próximo login. */
    const [, setVersaoUsuario] = useState(0);
    const caminhoAtual = useRef(location.pathname);
    caminhoAtual.current = location.pathname;
    useEffect(() => {
        const aoVoltarParaAba = () => {
            if (document.visibilityState === 'visible') void relerUsuario();
        };
        const aoMudarUsuario = () => {
            setVersaoUsuario(v => v + 1);
            const permissoes = getUserPermissions();
            if (!canAccessRoute(caminhoAtual.current, permissoes)) {
                navigate(getFirstAccessibleRoute(permissoes), { replace: true });
            }
        };
        const aoRecusarFuncionalidade = () => { void relerUsuario(true); };
        /* 403 PERMISSAO_NEGADA: só avisa. Não desloga nem relê o Auth/Me — a
           permissão nova chega no próximo refresh-token. O `id` fixo faz a
           recusa em rajada (ou a tela que também mostra a mensagem do corpo)
           virar um toast só. */
        const aoNegarPermissao = () => {
            aviso.erro('Você não tem permissão para esta ação.', { id: 'permissao-negada' });
        };

        document.addEventListener('visibilitychange', aoVoltarParaAba);
        window.addEventListener(USUARIO_ATUALIZADO_EVENT, aoMudarUsuario);
        window.addEventListener(FUNCIONALIDADE_DESLIGADA_EVENT, aoRecusarFuncionalidade);
        window.addEventListener(PERMISSAO_NEGADA_EVENT, aoNegarPermissao);
        return () => {
            document.removeEventListener('visibilitychange', aoVoltarParaAba);
            window.removeEventListener(USUARIO_ATUALIZADO_EVENT, aoMudarUsuario);
            window.removeEventListener(FUNCIONALIDADE_DESLIGADA_EVENT, aoRecusarFuncionalidade);
            window.removeEventListener(PERMISSAO_NEGADA_EVENT, aoNegarPermissao);
        };
    }, [navigate]);

    /* O tour de boas-vindas saiu em 26/09, a pedido dele ("tira ela por
       enquanto"), junto com o redesign de `/primeiros-passos`. Eram três
       balões ancorados em `id` de item da sidebar — e o terceiro já tinha
       envelhecido: mandava "altere sua senha por aqui" apontando para um botão
       que hoje abre um menu, porque a modal de perfil virou
       `/configuracoes/conta`. Quem faz o papel de onboarding agora é a tela
       `/primeiros-passos`, que lê o estado real da conta.
       O histórico está no git: `OnboardingModal`, `OnboardingTooltip` e
       `OnboardingContext` foram apagados neste mesmo commit. */

    /* O aviso de lead novo no aparelho segue quem está logado: se este navegador já estava
       inscrito, a inscrição passa a ser desta pessoa. */
    useEffect(() => { sincronizarAviso(); }, []);

    // Handlers
    const handleLogout = async () => {
        // Antes do logout, enquanto a sessão vale: o aparelho para de receber os leads de quem saiu.
        await soltarAvisoNoLogout();
        try {
            await fetch(`${import.meta.env.VITE_API_URL}/api/auth/logout`, {
                method: 'POST',
                credentials: 'include',
            });
        } catch {
            // ignora erro de rede — prossegue com limpeza local
        }
        localStorage.removeItem('user');
        navigate('/login');
    };

    return (
        <GlobalNotificationProvider>
            <PrimeirosPassosProvider>
            {/* O agente ao lado da tela: a porta mora no cabeçalho de cada tela
                (PageHeader) e no do celular (MobileNav); o painel, aqui, por
                portal — por cima de tudo, menos dos diálogos. */}
            <AgenteProvider>
            <AgentePainel />
            {/* A novidade do produto (o vídeo do lançamento). Aqui, e não na Home, para
                valer em qualquer tela do app. Ver `config/novidades.ts`. */}
            <NovidadeModal />
            <div className="app-layout">
                <SupportModeBanner />
                {/* A Topbar saiu e a sidebar vai até o topo. No celular ela some,
                    e quem navega é o `MobileNav` — cabeçalho, sino e gaveta. Ele
                    lê as mesmas listas da sidebar e do trilho, então tela nova
                    aparece lá sozinha. Precisa ficar DENTRO dos providers para o
                    sino e o contador de Primeiros passos terem os números. */}
                <MobileNav onLogout={handleLogout} />
                {/* Só no celular, até ativar ou dispensar: a porta do aviso de lead novo. */}
                <AvisosNoAparelhoBanner />

                {/* A paleta do Ctrl+K. Mora aqui, e não na sidebar, porque é
                    desenhada por portal em `document.body`: assim ela existe
                    também no celular (onde não há sidebar) e em Configurações
                    (onde o trilho ocupa o lugar dela). Ver `BuscaGlobal.tsx`. */}
                <BuscaGlobal onLogout={handleLogout} />

                <div className="main-body">
                    {/* O "modo": em rota de configuração o trilho ocupa o lugar
                        da sidebar, e a navegação de operação sai de vista. */}
                    {emConfiguracoes
                        ? <ConfiguracoesRail voltarPara={voltarPara} />
                        : <Sidebar onLogout={handleLogout} />}
                    <SubscriptionExpiredModal
                        isOpen={isSubscriptionExpiredOpen}
                        onClose={() => setIsSubscriptionExpiredOpen(false)}
                        expirationDate={dataExpiracaoPlano}
                    />
                    <main className="main-content" style={{ padding: 0 }}>
                        <Outlet />
                    </main>
                </div>

                {/* <NvoipDialer /> */}
            </div>
            </AgenteProvider>
            </PrimeirosPassosProvider>
        </GlobalNotificationProvider>
    );
};

export default MainLayout;
